import { useEffect, useRef, useState } from "react";
import { api, apiEstoque, apiPermissoes, type BuscaResultado, type HistoricoResp, type LoteAcerto, type MovimentoLinha } from "../lib/api";
import { type Usuario } from "../lib/auth";
import { Cabecalho } from "./ProducaoPcf";
import { Typeahead } from "../components/Typeahead";
import { MsgModal } from "../components/MsgModal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// Menu COMPRAS-ESTOQUE-PRODUCAO › ESTOQUE: c1logest, c2logest, atcadace.

const nf = (c: number) => new Intl.NumberFormat("pt-BR", { minimumFractionDigits: c, maximumFractionDigits: c });
const f3 = (n: number) => nf(2).format(n || 0);
// c1logest/c2logest: colunas de peso com máscara ---.---.--9,9999 no form (4 casas)
const f4 = (n: number) => nf(2).format(n || 0);
const f0 = (n: number) => nf(0).format(n || 0);
// quantidade do kardex pela unidade do lote: c1logest (com unidade) -> KG 4 casas / peças inteiro; c2logest -> 4 casas
const fq = (l: MovimentoLinha, v: number) => (l.unidade !== undefined && l.unidade !== "KG" ? f0(v) : f4(v));
const soDig = (v: string) => v.replace(/\D/g, "");
const soDec = (v: string, casas: number) => {
  // ponto do teclado numérico vira vírgula (com vírgula presente, ponto é milhar e sai)
  let s = (v.includes(",") ? v.replace(/\./g, "") : v.replace(/\./g, ",")).replace(/[^0-9,]/g, "");
  const i = s.indexOf(",");
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, "").slice(0, casas);
  return s;
};
const parseDec = (s: string) => Number(String(s).replace(/\./g, "").replace(",", ".")) || 0;

// Enter = Tab dentro do formulário.
function enterTab(e: React.KeyboardEvent, raiz: HTMLElement | null) {
  if (e.key !== "Enter" || (e.target as HTMLElement).tagName === "BUTTON") return;
  e.preventDefault();
  const foc = Array.from(raiz?.querySelectorAll<HTMLElement>("input,select,textarea,button") ?? []).filter(
    (x) => !(x as HTMLInputElement).disabled && x.tabIndex !== -1 && x.offsetParent !== null
  );
  const i = foc.indexOf(e.target as HTMLElement);
  if (i >= 0 && i < foc.length - 1) foc[i + 1]?.focus();
}

function TabelaMovimentos({ linhas, comTipo, comLote, colHistorico }: { linhas: MovimentoLinha[]; comTipo?: boolean; comLote?: boolean; colHistorico: string }) {
  const ord = useOrdenacao(linhas, {
    data: (l) => (l.data ? `${l.data} ${l.hora ?? ""}`.trim() : null), hora: (l) => l.hora, tipo: (l) => l.tipo,
    lote: (l) => l.numlot, und: (l) => l.unidade,
    hist: (l) => l.historico, ant: (l) => l.pesoAnterior, mov: (l) => l.pesoMovimentado, atu: (l) => l.pesoAtual,
    usu: (l) => l.usuario, loc: (l) => l.local,
  });
  return (
    <div className="tabela-wrap">
      <table className="tabela-pcfs">
        <thead>
          <tr>
            <ThOrdena ord={ord} col="data">Data</ThOrdena>
            <ThOrdena ord={ord} col="hora">Hora</ThOrdena>
            {comTipo && <ThOrdena ord={ord} col="tipo">Tipo</ThOrdena>}
            {comLote && <ThOrdena ord={ord} col="lote" className="num">Num. Lote</ThOrdena>}
            {comLote && <ThOrdena ord={ord} col="und">Und.</ThOrdena>}
            <ThOrdena ord={ord} col="hist">{colHistorico}</ThOrdena>
            <ThOrdena ord={ord} col="ant" className="num">{comLote ? "Qtd. Anterior" : "Peso Anterior"}</ThOrdena>
            <ThOrdena ord={ord} col="mov" className="num">{comLote ? "Qtd. Movimentado" : "Peso Movimentado"}</ThOrdena>
            <ThOrdena ord={ord} col="atu" className="num">{comLote ? "Qtd. Atual" : "Peso Atual"}</ThOrdena>
            <ThOrdena ord={ord} col="usu">Usuário</ThOrdena>
            <ThOrdena ord={ord} col="loc">Local de Estoque</ThOrdena>
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <td colSpan={(comTipo ? 9 : 8) + (comLote ? 2 : 0)} className="plan-vazio">
                Nenhuma movimentação.
              </td>
            </tr>
          )}
          {ord.linhas.map((l) => (
            <tr key={ord.idx(l)}>
              <td>{l.data}</td>
              <td>{l.hora}</td>
              {comTipo && <td>{l.tipo}</td>}
              {comLote && <td className="num">{l.numlot}</td>}
              {comLote && <td>{l.unidade}</td>}
              <td>{l.historico}</td>
              {/* c1logest novo: quantidade pela unidade do lote (KG -> peso, 4 casas; peças -> inteiro) */}
              <td className="num">{fq(l, l.pesoAnterior)}</td>
              <td className={"num" + (l.saida ? " mov-saida" : "")}>{fq(l, l.pesoMovimentado)}</td>
              <td className="num">{fq(l, l.pesoAtual)}</td>
              <td>{l.usuario}</td>
              <td>{l.local}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================================================================ c1logest — HISTORICO DE LOTE
// Correção 26/10/2026 (Ricardo): sem Lote/Pesquisar/Limpar — a busca é toda no campo Produto, com lista dinâmica
// (nome/código do produto ou nº do lote). "TODOS OS LOTES" mantém o lote vazio/0 do legado (lista todos).
type LoteOpc = { numlot: number; codpro: number; nompro: string; unidade: string; nomloc: string };
const TODOS: LoteOpc = { numlot: 0, codpro: 0, nompro: "TODOS OS LOTES", unidade: "", nomloc: "" };
const rotuloLote = (l: LoteOpc) =>
  l.numlot ? `Lote ${l.numlot} — ${l.nompro}${l.codpro ? ` (${l.codpro})` : ""}${l.unidade ? ` · ${l.unidade}` : ""}${l.nomloc ? ` · ${l.nomloc}` : ""}` : l.nompro;

export function HistoricoLote({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [rotulo, setRotulo] = useState("");
  const [r, setR] = useState<HistoricoResp | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // botao-pesquisar (c1logest novo :133-189): lote 0 lista TODOS os lotes (a checagem "Lote não encontrado!" está
  // comentada no legado novo, :141-147).
  async function pesquisar(l: LoteOpc) {
    try {
      const x = await apiEstoque.historicoLote(l.numlot);
      if (!x.ok) return setErro(x.erro ?? "Falha na pesquisa.");
      setErro(x.truncado ? "Quantidade de Itens de Repasse é superior ao máximo permitido. Avisar a Orange Custom Systems" : null);
      setRotulo(l.numlot ? `Lote ${l.numlot} — ${x.nompro ?? l.nompro}` : TODOS.nompro);
      setR(x);
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Histórico de Lote" sub="Movimentações de estoque do lote" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="pcf-grid">
            <label className="pcf-campo" style={{ flex: "1 1 100%" }}>
              <span>Produto (nome, código do produto ou nº do lote)</span>
              <Typeahead<LoteOpc>
                autoFocus
                minimo={0}
                placeholder="Digite o produto ou o lote…"
                valorInicial={rotulo}
                buscar={async (q) => {
                  const lista = await apiEstoque.lotesBusca(q);
                  return q.trim() ? lista : [TODOS, ...lista];
                }}
                rotulo={rotuloLote}
                onEscolher={(l) => void pesquisar(l)}
              />
            </label>
          </div>
        </section>
        {r?.linhas && (
          <section className="pcf-sec">
            <TabelaMovimentos linhas={r.linhas} comLote colHistorico="Histórico" />
          </section>
        )}
      </div>
    </div>
  );
}

// ================================================================ c2logest — HISTORICO DE PCF
// Correção 26/10/2026 (Ricardo): sem Pesquisar/Limpar — a busca dispara ao sair do nº da PCF (Enter/Tab);
// o Cliente é lista dinâmica (nome do cliente/produto -> PCFs; escolher carrega).
export function HistoricoPcf({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [numpcf, setNumpcf] = useState("");
  const [nomcli, setNomcli] = useState("");
  const [r, setR] = useState<HistoricoResp | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const ultima = useRef(0); // PCF já pesquisada (evita repetir no blur)

  async function pesquisar(n: number) {
    if (!n || n === ultima.current) return;
    ultima.current = n;
    try {
      const x = await apiEstoque.historicoPcf(n);
      if (!x.ok) {
        // mantém ultima = n: o foco no OK do aviso não repete a pesquisa (só repete ao mudar o número)
        setErro(x.erro ?? "PCF não encontrada!");
        setNomcli("");
        setR(null);
        ref.current?.focus();
        return;
      }
      setErro(x.truncado ? "Quantidade de Itens de Repasse é superior ao máximo permitido. Avisar a Orange Custom Systems" : null);
      setNomcli(x.nomcli ?? "");
      setR(x);
    } catch (ex) {
      ultima.current = 0;
      setErro((ex as Error).message);
    }
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Histórico de PCF" sub="Matéria-prima (MP) e produto fabricado (PF) da PCF" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>PCF</span>
              <input
                ref={ref}
                className="num"
                inputMode="numeric"
                autoFocus
                value={numpcf}
                onChange={(e) => {
                  setNumpcf(soDig(e.target.value));
                  if (ultima.current) {
                    ultima.current = 0;
                    setNomcli("");
                    setR(null);
                  }
                }}
                onBlur={() => void pesquisar(Number(numpcf))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void pesquisar(Number(numpcf));
                  }
                }}
              />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Cliente</span>
              <Typeahead<BuscaResultado>
                placeholder="Digite o cliente ou o produto…"
                valorInicial={nomcli}
                buscar={(q) => (q.trim().length < 2 ? Promise.resolve([]) : api.busca(q))}
                rotulo={(p) => `PCF ${p.numpcf} — ${p.cliente}${p.produto ? ` · ${p.produto}` : ""}${p.situacPcf ? ` · ${p.situacPcf}` : ""}`}
                onEscolher={(p) => {
                  setNumpcf(String(p.numpcf));
                  ultima.current = 0;
                  void pesquisar(p.numpcf);
                }}
              />
            </label>
          </div>
        </section>
        {r?.linhas && (
          <section className="pcf-sec">
            <TabelaMovimentos linhas={r.linhas} comTipo colHistorico="Produto" />
          </section>
        )}
      </div>
    </div>
  );
}

// ================================================================ atcadace — ACERTOS DE ESTOQUE / ALT DE CORRIDA
export function AcertoEstoque({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [numlot, setNumlot] = useState("");
  const [lote, setLote] = useState<LoteAcerto | null>(null);
  const [corrida, setCorrida] = useState("");
  const [tipo, setTipo] = useState<"E" | "S">("E");
  const [qtd, setQtd] = useState("");
  const [historico, setHistorico] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const loteRef = useRef<HTMLInputElement>(null);
  const corridaRef = useRef<HTMLInputElement>(null);
  // perpro ATCADACE 'Inativo' (ou nó pai): a tela não abre — a trava real é no servidor (403).
  const [semPerm, setSemPerm] = useState<string | null>(null);
  useEffect(() => {
    apiPermissoes()
      .then((p) => p.bloqueados.includes("ATCADACE") && setSemPerm("Usuário sem permissão para Acertos de Estoque (ATCADACE)."))
      .catch((ex) => setSemPerm((ex as Error).message));
  }, []);

  // MOSTRAR-LOTE
  async function mostrar(n: number) {
    if (!n) return;
    try {
      const x = await apiEstoque.aceLote(n);
      setNumlot(String(n));
      if (x.erro) {
        setErro(x.erro);
        setLote(null);
        loteRef.current?.focus();
        return;
      }
      setErro(null);
      setLote(x);
      setCorrida(x.corrida);
      setQtd("");
      setTimeout(() => corridaRef.current?.focus(), 0);
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  function limpar() {
    setNumlot("");
    setLote(null);
    setCorrida("");
    setTipo("E");
    setQtd("");
    setHistorico("");
    setErro(null);
    loteRef.current?.focus();
  }

  // SOMAR-SUBTRAIR-ESTOQUE: saldo final = saldo ± quantidade.
  const qtdNum = parseDec(qtd);
  const final = lote ? lote.saldo + (tipo === "S" ? -qtdNum : qtdNum) : 0;
  const fq = (n: number) => (lote?.ehKg ? f3(n) : f0(n));

  async function gravar() {
    const n = Number(numlot);
    if (!n) {
      setErro("Lote não informado!");
      loteRef.current?.focus();
      return;
    }
    setGravando(true);
    try {
      const r = await apiEstoque.aceGravar({ numlot: n, corrida, qtdace: qtdNum, tipo, historico });
      if (!r.ok) {
        setErro(r.mensagem);
        return;
      }
      setOk(qtdNum ? `Lote ${n}: acerto de estoque gravado.` : `Lote ${n}: corrida gravada.`);
      limpar();
    } catch (ex) {
      setErro((ex as Error).message);
    } finally {
      setGravando(false);
    }
  }

  if (semPerm)
    return (
      <div className="app">
        <Cabecalho usuario={usuario} titulo="Acertos de Estoque / Alteração de Corrida" sub="Entrada ou saída manual no lote" onVoltar={onVoltar} />
        <MsgModal texto={semPerm} tipo="erro" onClose={onVoltar} />
      </div>
    );

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Acertos de Estoque / Alteração de Corrida" sub="Entrada ou saída manual no lote" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <div className="pcf-form" ref={formRef} onKeyDown={(e) => enterTab(e, formRef.current)}>
        <section className="pcf-sec">
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>Lote</span>
              <input
                ref={loteRef}
                className="num"
                inputMode="numeric"
                autoFocus
                value={numlot}
                onChange={(e) => {
                  setNumlot(soDig(e.target.value));
                  setOk(null);
                }}
                onBlur={() => Number(numlot) !== (lote?.numlot ?? 0) && void mostrar(Number(numlot))}
              />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Produto</span>
              <input value={lote?.nompro ?? ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo">
              <span>Local de Estoque</span>
              <input value={lote?.nomloc ?? ""} readOnly tabIndex={-1} />
            </label>
          </div>
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>Comprimento Inicial</span>
              <input className="num" value={lote ? f3(lote.compIni) : ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Comprimento Final</span>
              <input className="num" value={lote ? f3(lote.compFim) : ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Unidade</span>
              <input value={lote?.unidade ?? ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>{lote ? (lote.ehKg ? "Saldo Atual (KG)" : "Saldo Atual (Peças)") : "Saldo"}</span>
              <input className="num" value={lote ? fq(lote.saldo) : ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo">
              <span>Corrida</span>
              <input ref={corridaRef} value={corrida} maxLength={30} onChange={(e) => setCorrida(e.target.value)} />
            </label>
          </div>
        </section>
        <section className="pcf-sec">
          <div className="pcf-grid">
            <div className="pcf-campo pcf-sm">
              <span>Tipo do Acerto</span>
              <div className="ace-tipo">
                <label>
                  <input type="radio" name="tipace" checked={tipo === "E"} onChange={() => setTipo("E")} /> Entrada
                </label>
                <label>
                  <input type="radio" name="tipace" checked={tipo === "S"} onChange={() => setTipo("S")} /> Saída
                </label>
              </div>
            </div>
            <label className="pcf-campo pcf-sm">
              {/* tela-1.prc L258-270: KG -> "Peso" com ZZZ.ZZ9,999; senão "Quantidade" com ZZZ.ZZ9.
                  Sem lote vale o caption do form ("Quantidade"). */}
              <span>{lote?.ehKg ? "Peso" : "Quantidade"}</span>
              <input
                className="num"
                inputMode={lote && !lote.ehKg ? "numeric" : "decimal"}
                value={qtd}
                onChange={(e) => setQtd(lote && !lote.ehKg ? soDig(e.target.value) : soDec(e.target.value, 3))}
              />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Estoque Final</span>
              <input className="num" value={lote && qtd ? fq(final) : ""} readOnly tabIndex={-1} />
            </label>
          </div>
          <div className="pcf-grid">
            <label className="pcf-campo" style={{ flex: "1 1 100%" }}>
              <span>Histórico</span>
              {/* multilinha (T1-HISTOR-WS, PIC X(100)); Enter = Tab como nos demais campos */}
              <textarea rows={3} value={historico} maxLength={100} onChange={(e) => setHistorico(e.target.value.replace(/\r?\n/g, " "))} />
            </label>
          </div>
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao" disabled={gravando} onClick={() => void gravar()}>
            Gravar
          </button>
          <button className="btn-secundario" onClick={limpar}>
            Limpar
          </button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
